import { z } from 'zod';
import { apiGet, apiSend } from '@/lib/api';

/**
 * Podgrupa towarowa.
 *
 * ⚠️ `isgid` to KLUCZ NATURALNY (`ItemSubGroups.ISGID`), złożona wartość `'{grupa}_{podgrupa}'`.
 * `code` sam z siebie kluczem NIE JEST i nie musi być unikalny poza swoją grupą — dlatego
 * `Items.ITEMSUBGROUP` trzyma `isgid`, a nie `code`.
 */
export const SubGroupNodeSchema = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  isgid: z.string().nullable(),
  items: z.number(),
  obsoleteItems: z.number(),
  machines: z.number(),
  pending: z.boolean(),
  /** Kafelek podgrupy w SPSUI (`ItemSubGroups.IMAGEFILE`). `optional`: starsze API go nie oddaje. */
  imageFile: z.string().nullable().optional(),
  hasImage: z.boolean().optional(),
});

export type SubGroupNode = z.infer<typeof SubGroupNodeSchema>;

export const GroupNodeSchema = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  /** Towary przypisane BEZPOŚREDNIO do grupy głównej, bez podgrup. */
  items: z.number(),
  obsoleteItems: z.number(),
  machines: z.number(),
  /** Grupa razem z podgrupami — po tym liczy się „grupa pusta". */
  subtreeItems: z.number(),
  subtreeMachines: z.number(),
  /** Reguły limitów celujące w kod TEJ grupy (`Rationing.ITEMTYPE=8`). */
  rationingRules: z.number(),
  pending: z.boolean(),
  /** Kafelek grupy w SPSUI (`ItemGroups.IMAGEFILE`, 27.10.2026) i czy biblioteka ma jego bajty. */
  imageFile: z.string().nullable().optional(),
  hasImage: z.boolean().optional(),
  subGroups: z.array(SubGroupNodeSchema),
});

export type GroupNode = z.infer<typeof GroupNodeSchema>;

const TreeSchema = z.object({
  groups: z.array(GroupNodeSchema),
  summary: z.object({
    groups: z.number(),
    subGroups: z.number(),
    itemsWithoutGroup: z.number(),
    itemsInSubGroups: z.number(),
    emptyGroups: z.number(),
    rationingOnGroups: z.number(),
    /**
     * Limity celujące w kod, którego nie ma żadna grupa główna tej wyspy.
     * SPS nie ma kluczy obcych, więc taki wiersz jest tam legalny — i cichy.
     */
    rationingOrphans: z.number(),
  }),
});

export const fetchItemGroupTree = (groupId: string, search: string, includeEmpty: boolean) => {
  const q = new URLSearchParams({ groupId, includeEmpty: String(includeEmpty) });
  if (search) q.set('search', search);
  return apiGet(`/item-groups?${q.toString()}`, TreeSchema);
};

const TakenSchema = z.object({ id: z.string(), code: z.string(), name: z.string() }).nullable();

/**
 * Czy kod jest wolny.
 *
 * Przy podgrupie odpowiedź ma DWA pola kolizji, nie jedno: `takenBy` (ten kod już jest w tej
 * grupie) i `isgidTakenBy` (złożony klucz jest zajęty). Drugie może trafić nawet gdy pierwsze
 * jest puste — bo `ISGID` powstaje ze zlepienia dwóch kodów.
 */
export const checkGroupCode = (groupId: string, code: string, parentId?: string) => {
  const q = new URLSearchParams({ groupId, code });
  if (parentId) q.set('parentId', parentId);
  return apiGet(
    `/item-groups/check-code?${q.toString()}`,
    z.object({
      code: z.string(),
      /** Podglądowa wartość złożonego klucza — `null` dla grupy głównej. */
      isgid: z.string().nullable(),
      free: z.boolean(),
      takenBy: TakenSchema,
      isgidTakenBy: TakenSchema,
      pending: z.boolean(),
      /**
       * Ten kod był już kiedyś usuwany z tej wyspy.
       *
       * Kaskada usunięcia NIE czyści wpisów dostępu (`Restrictions2`), bo tej tabeli nie ma
       * w read-modelu — więc osierocone wpisy ze starym kodem zostały w bazie klienta i grupa
       * utworzona na tym samym kodzie przyklei je do siebie.
       */
      previouslyDeleted: z.boolean(),
    }),
  );
};

const DeleteImpactSchema = z.object({
  node: z.object({
    id: z.string(),
    code: z.string(),
    name: z.string(),
    isgid: z.string().nullable(),
    isSubGroup: z.boolean(),
    /** Kod grupy, do której WRÓCĄ towary po usunięciu podgrupy; `null` przy grupie głównej. */
    fallbackGroupCode: z.string().nullable(),
  }),
  items: z.array(z.object({ id: z.string(), itemNumber: z.string(), name: z.string() })),
  subGroups: z.array(z.object({ id: z.string(), code: z.string(), name: z.string(), isgid: z.string().nullable() })),
  rationing: z.array(
    z.object({
      itemKey: z.string(),
      issueTargetType: z.number(),
      issueTargetKey: z.string(),
      maxQty: z.number(),
      period: z.number(),
      periodType: z.number(),
    }),
  ),
  /**
   * Wpisy dostępu do towarów (`Restrictions2`) wskazujące ten węzeł RAZEM Z PODGRUPAMI.
   *
   * `dConsumer` to MASKA POZYCYJNA (6 pozycji: rUser, rJob, rMachine, rAux1, rAux2, rAux3;
   * `3` = pojedyncza encja, `2` = grupa), a `dItem` pojedyncza wartość (`2` grupa, `3` towar,
   * `4` podgrupa). Portal NIE mówi „odbierze dostęp": bez flag `REST_ENABLED`/`REST_DENY`
   * z `SetupData` nie wiadomo, czy wpis zezwalał, czy ZAKAZYWAŁ.
   */
  restrictions: z.array(
    z.object({
      rUser: z.string(),
      rJob: z.string(),
      rMachine: z.string(),
      rAux1: z.string(),
      rAux2: z.string(),
      rAux3: z.string(),
      dConsumer: z.string(),
      rItem: z.string(),
      dItem: z.string(),
    }),
  ),
  counts: z.object({
    items: z.number(),
    subGroups: z.number(),
    rationing: z.number(),
    restrictions: z.number(),
  }),
  pending: z.boolean(),
});

export type DeleteImpact = z.infer<typeof DeleteImpactSchema>;

/** CO ZNIKNIE razem z grupą — pobierane PRZED pokazaniem pytania, nie po. */
export const fetchDeleteImpact = (id: string, groupId: string) =>
  apiGet(`/item-groups/${id}/delete-impact?groupId=${groupId}`, DeleteImpactSchema);

/**
 * Usunięcie grupy albo podgrupy — kaskada po PIĘCIU tabelach (z wpisami dostępu).
 *
 * `ackAssumedRestrictionKey` jest WYMAGANE i dotyczy dwóch rzeczy: klucz `Restrictions2` jest
 * w naszym mapowaniu ZAŁOŻONY, nie potwierdzony żadnym wykonanym zapisem, a kaskada usuwa tylko
 * te wpisy, KTÓRE ZNA READ-MODEL.
 */
export const deleteItemGroup = (id: string, groupId: string) =>
  apiSend('DELETE', `/item-groups/${id}`, { groupId, ackAssumedRestrictionKey: true }, OpSchema);

const OpSchema = z.object({ opId: z.string() });

/**
 * CO RUSZYŁABY zmiana kodu grupy głównej — policzone, ale NIEWYKONALNE z portalu.
 *
 * Odpowiedź niesie DWIE sumy, nie jedną, i to jest sedno tego ekranu: `totalRows` to zakres
 * NASZEJ operacji, `totalRowsSpsParity` to zakres kaskady SAMEGO SPS. Różnica jest naszym
 * rozszerzeniem (decyzja D2 — bez tych kroków zostają sieroty), a `collateralRows` to wiersze
 * przepisywane „przy okazji", bo SPS nie ma w tym miejscu dyskryminatora (decyzja D1).
 * Jedna wspólna liczba ukryłaby oba te fakty.
 */
const RenameImpactSchema = z.object({
  node: z.object({ id: z.string(), code: z.string(), name: z.string() }),
  targets: z.array(
    z.object({
      table: z.string(),
      column: z.string(),
      rows: z.number(),
      note: z.string(),
      /** przepisywane przy okazji, bo SPS nie filtruje tu po dyskryminatorze (D1) */
      collateral: z.literal(true).optional(),
      /** krok, którego kaskada SPS NIE MA — nasze rozszerzenie ponad parytet (D2) */
      beyondSps: z.literal(true).optional(),
    }),
  ),
  totalRows: z.number(),
  totalRowsSpsParity: z.number(),
  collateralRows: z.number(),
  beyondSpsRows: z.number(),
  tablesTouched: z.number(),
  allTablesInReadModel: z.boolean(),
  executable: z.boolean(),
  blockers: z.array(z.string()),
});

export type RenameImpact = z.infer<typeof RenameImpactSchema>;

export const fetchRenameImpact = (id: string, groupId: string) =>
  apiGet(`/item-groups/${id}/rename-impact?groupId=${groupId}`, RenameImpactSchema);

export const createItemGroup = (groupId: string, code: string, name: string) =>
  apiSend('POST', '/item-groups', { groupId, code, name }, OpSchema.extend({ id: z.string(), code: z.string() }));

/** `ISGID` składa SERWER z kodu grupy nadrzędnej — klient go nie wysyła. */
export const createSubGroup = (parentId: string, groupId: string, code: string, name: string) =>
  apiSend(
    'POST',
    `/item-groups/${parentId}/subgroups`,
    { groupId, code, name },
    OpSchema.extend({ id: z.string(), isgid: z.string() }),
  );

/**
 * Zmiana NAZWY. Kodu tu nie ma i nie będzie przez pomyłkę.
 *
 * `Items` wskazuje grupę tekstową wartością kodu, a SPS nie ma kluczy obcych — zmiana kodu to
 * przepisanie `Items.ITEMGROUP`, `Items.ITEMSUBGROUP`, `ItemSubGroups.ITEMGROUP`,
 * `ItemSubGroups.ISGID` i `Rationing.ITEMKEY`. To osobna operacja, nie patch pola.
 */
export const renameItemGroup = (id: string, groupId: string, name: string) =>
  apiSend('PATCH', `/item-groups/${id}`, { groupId, name }, OpSchema);

/**
 * Zmiana KODU grupy głównej — klucza, nie nazwy.
 *
 * ⚠️ Nie mylić z `renameItemGroup`, które zmienia `DESCR` jednego wiersza. To uruchamia kaskadę
 * po siedmiu tabelach SPS i rozwija się na podgrupy, bo trzy z tych kolumn niosą złożony klucz
 * `{grupa}_{podgrupa}`.
 */
export const zmienKodGrupyTowarowej = (id: string, payload: { groupId: string; nowyKod: string }) =>
  apiSend('POST', `/item-groups/${id}/code`, payload, OpSchema.extend({ stary: z.string(), nowy: z.string() }));
